import React, { useEffect } from "react";
import { Config } from "Config";
import Pendo from "helpers/Pendo";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { bootstrap, selectSession } from "redux/slices/session";
import Pubnub from "pubnub";
import { PubNubProvider } from "pubnub-react";
import localForage from "localforage";
import { LocalForageStores } from "local-forage";
import * as Sentry from "@sentry/react";
import { useSnackbar } from "notistack";
import { LD_CONTEXT_GLOBAL } from "LD_CONTEXT";

const pubnub = new Pubnub({
  subscribeKey: Config.pubnubSubscribeKey,
  uuid: Pubnub.generateUUID(),
});

type VendorProps = {
  children: React.ReactNode;
};

const VendorSetup = (props: VendorProps) => {
  const ldClient = useLDClient();

  const session = useAppSelector(selectSession);
  const { versionBanner } = useFlags();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (session) {
      localForage.config({
        name: "pattern",
        version: 1.0,
        storeName: LocalForageStores.Lens,
        description: "User settings for lenses and forms",
      });

      dispatch(bootstrap())
        .unwrap()
        .then((response) => {
          const info = response.users.self;
          const client = response.client.data;
          Sentry.setUser({
            email: info.email,
          });
          Pendo.initialize(
            session,
            parseInt(Config.version),
            info.userType,
            info.firstName,
            info.lastName,
            client.clientType
          );
          if (ldClient) {
            const ldContext = {
              key: `${session.userId}`,
              kind: "user",
              email: info.email,
              anonymous: false,
              clientId: info.clientId,
              appVersion: parseInt(Config.version),
              userType: info.userType,
              clientType: client.clientType,
            };
            ldClient.identify(ldContext);
            LD_CONTEXT_GLOBAL.ldContext = ldContext;
          }
        });
    }
  }, [session, ldClient]);

  useEffect(() => {
    if (versionBanner) {
      enqueueSnackbar("", { variant: "refresh", persist: true });
    }
  }, [versionBanner]);

  return <PubNubProvider client={pubnub}>{props.children}</PubNubProvider>;
};

export default VendorSetup;
